import { useState, useEffect } from 'react';
import { AccountType, useSession } from '@nextgen-web-framework/all';
import {
  AccountProcessType,
  MyShopStorageConstant,
  AccountProcess,
  getProductCode,
  InitialCartState,
  cartApi,
  CartConstantsEnum,
  OrderType,
  Cart,
  updateCart,
} from '@commerce-cart-webcomponents/sdk';
import { AdvanceShareCodeResponse } from '@commerce-webcomponents/sdk';
import { RegistrationBanner } from '@commerce-profile-webcomponents/ui';
import { ProfileConstantsEnum } from '@commerce-profile-webcomponents/sdk';
import { ConsoleLogger, StorageService } from '@nextgen-web-framework/core';
import { useSelector, useDispatch } from 'react-redux';
import type { AppState } from '@nextgen-web-framework/core';
import { IProduct, ProductData, productApi, productDataTransformer } from '@commerce-product-webcomponents/sdk';

export type AccountRegistrationBannerProps = {
  registrationFee?: string;
  isListBanner?: boolean;
  accountProcessDetails?: AccountProcess;
  registrationFeesCb?: (fees: number) => void;
};

export type registrationAttributesType = {
  accountType: string;
  sponsor: {
    accountName: string;
    accountNumber: string;
    location: string;
  };
};
export const AccountRegistrationBanner = ({
  accountProcessDetails,
  registrationFee = '',
  isListBanner = false,
  registrationFeesCb,
}: AccountRegistrationBannerProps) => {
  const { isAnonymous } = useSession();
  const dispatch = useDispatch();
  const localstorage = StorageService.getLocalStorage();
  const shareCode = localstorage.retrieve(MyShopStorageConstant.SHARECODEATTRIBUTES) as AdvanceShareCodeResponse;
  const cartId = localstorage.retrieve(CartConstantsEnum.CartId);
  const profileDetails = localstorage.retrieve(
    ProfileConstantsEnum.registrationAttributes,
  ) as unknown as registrationAttributesType;

  const [product, setProduct] = useState({} as IProduct);
  const commerceCartState = useSelector((state: AppState) => state.commerceCart);
  const { cart }: InitialCartState = commerceCartState;

  useEffect(() => {
    if (isListBanner) {
      const { productCode } = getProductCode(shareCode?.registrationAccountType || '');
      getProductData(productCode);
    }
  }, []);

  const getProductData = (productCode: string) => {
    productApi
      .getProductByIdOrError({
        productId: productCode,
        warehouseId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.WarehouseId),
      })
      .then((productData: ProductData | Response) => {
        if (!(productData instanceof Response)) {
          setProduct(productDataTransformer(productData));
          registrationFeesCb?.(productDataTransformer(productData).productPrice);
        }
      })
      .catch((e) => {
        ConsoleLogger.log(e);
      });
  };

  const callUpdateCart = async (updatedAccountType: AccountType) => {
    const res = (await cartApi.updateCart(cartId, cartId, {
      accountProcess: {
        type: AccountProcessType.Registration,
        businessNature: updatedAccountType,
      },
      orderType: OrderType.Regular,
      version: +StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion),
    })) as Cart;
    dispatch(updateCart(res));
  };

  return (
    <>
      {(accountProcessDetails?.type === AccountProcessType.Registration ||
        (isListBanner && shareCode?.registrationAccountType)) &&
        isAnonymous() && (
          <RegistrationBanner
            accountType={
              isListBanner
                ? (shareCode?.registrationAccountType as AccountType)
                : (accountProcessDetails?.businessNature as AccountType)
            }
            callback={(accountType: AccountType) => {
              shareCode.registrationAccountType = accountType;
              localstorage.store(MyShopStorageConstant.SHARECODEATTRIBUTES, shareCode);
              if (!isListBanner) {
                callUpdateCart(accountType);
                return;
              }
              const { productCode } = getProductCode(accountType);
              getProductData(productCode);
            }}
            sponsorNumber={
              cart?.accountProcess?.id ? profileDetails?.sponsor?.accountNumber : shareCode?.accountId || ''
            }
            sponsorName={cart?.accountProcess?.id ? profileDetails?.sponsor?.accountName : shareCode?.accountName || ''}
            fee={isListBanner ? String(product.productPrice || '') : registrationFee}
            isEditable={cart && !cart.accountProcess?.id}
          />
        )}
    </>
  );
};
